import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { Card, CardContent } from '@/shared/components/ui/card';
import type { TeamMemberWorkloadDto } from '../../types';

interface TeamWorkloadRowProps {
  member: TeamMemberWorkloadDto;
}

function getInitials(name: string | null): string {
  if (!name) return '?';
  return name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

function formatHours(decimal: number): string {
  const h = Math.floor(decimal);
  const m = Math.round((decimal - h) * 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export function TeamWorkloadRow({ member }: TeamWorkloadRowProps) {
  const { t } = useTranslation('analytics');

  const loadPct =
    member.totalTasks > 0 ? Math.round((member.inProgressTasks / member.totalTasks) * 100) : 0;

  return (
    <Card data-testid={`analytics-team-workload-row-${member.userId ?? 'unknown'}`}>
      <CardContent className="px-4 py-3">
        <div className="flex items-start gap-3">
          <Avatar className="h-10 w-10 shrink-0">
            <AvatarImage src={member.userAvatarUrl ?? undefined} />
            <AvatarFallback className="text-sm font-semibold">
              {getInitials(member.userName)}
            </AvatarFallback>
          </Avatar>

          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
              <span className="text-sm font-semibold">{member.userName ?? '—'}</span>
              {member.overdueTasks > 0 && (
                <span className="flex items-center gap-0.5 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600 dark:bg-red-950 dark:text-red-400">
                  <AlertTriangle className="h-3 w-3" />
                  {member.overdueTasks} {t('workload.columns.overdue').toLowerCase()}
                </span>
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              {t('workload.stats.total')}:{' '}
              <strong className="text-foreground">{member.totalTasks}</strong>
              {' · '}
              {t('workload.stats.done')}:{' '}
              <strong className="text-blue-600 dark:text-blue-400">{member.doneTasks}</strong>
              {' · '}
              {t('workload.stats.active')}:{' '}
              <strong className="text-cyan-600 dark:text-cyan-400">{member.inProgressTasks}</strong>
              {' · '}
              {t('workload.stats.efficiency')}:{' '}
              <strong className="text-primary">{member.efficiencyPercentage.toFixed(0)}%</strong>
              {' · '}
              {t('workload.stats.time')}:{' '}
              <strong className="text-foreground">{formatHours(member.totalTrackedHours)}</strong>
            </p>
          </div>

          <div className="shrink-0 text-right">
            <p className="mb-1 text-xs text-muted-foreground">{t('workload.stats.load')}</p>
            <p className="text-sm font-semibold">{loadPct}%</p>
            <div className="mt-1 h-1.5 w-16 overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${loadPct}%` }}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
